import { ApolloClient, createHttpLink, InMemoryCache, from, Operation } from '@apollo/client';
import { RetryLink } from '@apollo/client/link/retry';
import { onError } from '@apollo/client/link/error';

import { setContext } from '@apollo/client/link/context';
import { clientEnv } from '@/config/env/client';
import fetch from 'cross-fetch';

const httpLink = createHttpLink({
  uri: `${clientEnv.NEXT_PUBLIC_INCLINIC_GATEWAY_URL}/graphql`,
  fetch,
});

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
    },
  };
});

// Only the variable names: values carry patient and verification data (emails, tax codes, OTP codes),
// and the console ends up in Sentry breadcrumbs attached to every later event.
export const getLoggableVariables = (operation: Pick<Operation, 'variables'>) => JSON.stringify(Object.keys(operation.variables ?? {}));

// Not idempotent: after a lost response a retry sends a second email (or gets a 5008 for the code that did arrive),
// and a verification retry spends an attempt on a code the backend may have already accepted and deleted.
const OPERATIONS_WITHOUT_RETRY = new Set(['SendCheckoutOtp', 'VerifyCheckoutOtp']);

export const isRetryableOperation = (operation: Pick<Operation, 'operationName'>) => !OPERATIONS_WITHOUT_RETRY.has(operation.operationName);

// Log any GraphQL errors or network error that occurred
const errorLink = onError(({ graphQLErrors, networkError, forward, operation }) => {
  let willRetry = false;

  if (graphQLErrors) {
    graphQLErrors.forEach(error => {
      const { message, locations, path, extensions } = error;
      if (message === 'Timeout has occurred' && extensions?.code === 'INTERNAL_SERVER_ERROR') {
        willRetry = true;
      }

      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path} variables:${getLoggableVariables(operation)}`,
      );
    });
  }
  if (networkError) console.log(`[Network error]: ${networkError}`);

  if (willRetry && isRetryableOperation(operation)) {
    console.log('[GraphQL retry operation]');
    return forward(operation);
  }
});

const retryLink = new RetryLink({
  delay: {
    initial: 10,
    max: 2000,
    jitter: true,
  },
  attempts: {
    max: 5,
    retryIf: (error, operation) => !!error && isRetryableOperation(operation),
  },
});

const client = new ApolloClient({
  link: from([retryLink, errorLink, authLink, httpLink]),
  cache: new InMemoryCache(),
});

export default client;
